import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Sentry } from './lib/sentry';
import { AppProviders } from './AppProviders';
import { AppRouter } from './AppRouter';
import { bootResolveTheme } from './features/theme';
import './styles/global.css';

// Resolve the persisted palette + primary-style BEFORE React mounts
// so the first paint already has the right `data-theme` /
// `data-primary-style` attributes on <html>. Without this users see
// one frame of the default editorial palette before their picked
// theme settles in.
bootResolveTheme();

const root = document.getElementById('root');
if (!root) {
  throw new Error('Root element not found in document. Check apps/web/index.html.');
}

createRoot(root).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => {
        const err = error as Error;
        return (
          <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 py-10 text-center">
            <h1 className="text-2xl font-semibold tracking-tight">Something went wrong.</h1>
            <p className="max-w-md text-sm text-muted-foreground">
              {import.meta.env.PROD
                ? 'We logged the error and the team has been notified. Try reloading the page.'
                : 'A render error escaped to the root. The full message is below — fix it and the page will hot-reload.'}
            </p>
            {!import.meta.env.PROD && err ? (
              <pre className="max-w-3xl overflow-auto rounded-md border border-line bg-card px-4 py-3 text-left text-xs leading-relaxed text-destructive">
                {err.message}
                {err.stack ? `\n\n${err.stack}` : ''}
              </pre>
            ) : null}
            <button
              type="button"
              onClick={() => {
                resetError();
                window.location.reload();
              }}
              className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground"
            >
              Reload
            </button>
          </div>
        );
      }}
    >
      <BrowserRouter>
        <AppProviders>
          <AppRouter />
        </AppProviders>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
